import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Anchor, Icon, IconTypes, PageContentWrapper, PageModal, PrimaryButton, PrimaryButtonIconTypes } from 'UI-Dash-Commons-UI';

const LegalModalComponent: React.FunctionComponent = () => {
    const [toggleOpen, setToggleOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(true);

    useEffect(() => {
        const termsAccepted = localStorage.getItem('al_data_sharing_terms_accepted');
        if (termsAccepted) {
            setModalOpen(false);
        }
    }, []);

    const handleContinueClick = () => {
        localStorage.setItem('al_data_sharing_terms_accepted', 'true');
        setModalOpen(false);
    }

    return !modalOpen ? null : (
        <PageModal isVisible={modalOpen} closeModal={() => { }} width={'70vw'} showCloseButton={false}>
            <PageContentWrapper>
                <Header>AppLovin Audience+ | Data Sharing</Header>
                <Text>
                    If you choose to use the Audience+, you will enable AppLovin's pixel, script, and/or other integrations (as applicable) to collect certain information about your customers' interactions with your properties ("Customer Data") and then pass that information to AppLovin for purposes of the Audience+ services.
                </Text>
                <Text>
                    When you use Audience+, you are fully responsible for complying with applicable privacy laws, including transparently describing your data sharing practices to your customers, collecting users' consent as applicable, and managing users' personal information.  Please solicit your own legal advice to determine how legal requirements apply to you. Nothing in this document should be construed as legal advice.
                </Text>

                <HighlightArea>
                    <Text>⚠️ Before you begin the integration process, install any Audience+ pixels, scripts, or other integrations (as applicable), and/or otherwise share any Customer Data with AppLovin, you must:</Text>
                    <List>
                        <BulletPoint>Review and abide by AppLovin's <StyledAnchor href="https://legal.applovin.com/privacy/" text="Privacy Policy" target="_blank" />, <StyledAnchor href="https://legal.applovin.com/terms/" text="Terms of Use Agreement" target="_blank" />, and <StyledAnchor href="https://legal.applovin.com/policies-demand-partners/" text="AppLovin Policies for Demand Partners" target="_blank" />.</BulletPoint>
                        <BulletPoint>Be sure that your privacy policy accurately discloses your collection, use, and sharing of Customer Data and other data with third parties like AppLovin, and can be easily accessed from the footer section of your website (and other locations).</BulletPoint>
                        <BulletPoint>List AppLovin in your privacy policy as a third-party that collects data, describe how data is shared with and used by third parties like AppLovin, and include the following statement and link in your privacy policy. "For more information about AppLovin's collection and use of your information visit: <StyledAnchor href="https://legal.applovin.com/privacy/" text="https://legal.applovin.com/privacy/" target="_blank" />."</BulletPoint>
                        <BulletPoint>Ensure that you comply with data and other limitations specific to your use of Audience+ and enablement of AppLovin's pixel, script, or other integrations, as described in the AppLovin technical integration documentation and in other documentation and communications from AppLovin.</BulletPoint>
                        <BulletPoint>Implement a compliant cookie banner (or comparable mechanism), disclose your enablement of AppLovin's pixel, script, or other integrations, and respect end user consent choices as required under applicable laws.</BulletPoint>
                        <BulletPoint>Ensure that no sensitive personal data (including children data) or custom events or objects will be sent to AppLovin.</BulletPoint>
                    </List>
                </HighlightArea>

                <ToggleWrapper onClick={() => setToggleOpen(!toggleOpen)}>
                    <StyledIcon iconType={IconTypes.UI_ARROW_RIGHT} alt={"right"} width={"8px"} rotated={toggleOpen} />
                    <TextToggle className="toggle">View Customer Data to be Collected by Audience+</TextToggle>
                </ToggleWrapper>
                <ToggleContent toggleOpen={toggleOpen}>
                    <Text>When you install the Audience+ custom pixel, script, and/or other integrations (as applicable), you enable collection of the following types of Customer Data:</Text>
                    <List>
                        <BulletPoint>Events data related to users' shopping behavior and browsing history, such as page views, add to cart, and checkouts. For additional information, please see the AppLovin <StyledAnchor href="https://developers.applovin.com/en/audience-plus/getting-started/" text="technical integration documentation" target="_blank" />.</BulletPoint>
                        <BulletPoint>IP address, device data, browser and page properties. For additional information, please refer to our <StyledAnchor href="https://legal.applovin.com/privacy/" text="Privacy Policy" target="_blank" />.</BulletPoint>
                    </List>
                </ToggleContent>

                <SmallHighlightArea>
                    <Text>I read and understand my obligations before enabling data sharing with AppLovin.</Text>
                    <StyledPrimaryButton text="Continue" iconType={PrimaryButtonIconTypes.NONE} onClick={handleContinueClick} />
                </SmallHighlightArea>
            </PageContentWrapper>
        </PageModal>
    );
};

export default LegalModalComponent;

const Header = styled.h1`
    font-family: "AL-AvenirNext-DemiBold", sans-serif;
    font-size: 30px;
    font-weight: bold;
    margin-top: 0;
`;

const Text = styled.p`
    font-family: "AL-AvenirNext-Regular", sans-serif;
`;

const ToggleWrapper = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
`;

const TextToggle = styled.p`
    font-family: "AL-AvenirNext-DemiBold", sans-serif;
    font-size: 22px;
    font-weight: bold;
`;

const StyledIcon = styled(Icon)`
    transform: ${props => props.rotated ? 'rotate(45deg)' : 'rotate(0deg)'};
    margin-top: ${props => props.rotated ? '5px' : '0'};
    margin-right: 10px;
`;

const ToggleContent = styled.div<{ toggleOpen?: boolean }>`
    display: ${props => props.toggleOpen ? 'block' : 'none'};
    padding-left: 20px;
    margin-top: -20px;
`;

const List = styled.ul`
    list-style-position: outside;
`;

const BulletPoint = styled.li`
    font-family: "AL-AvenirNext-Regular", sans-serif;
`;

const StyledAnchor = styled(Anchor)`
    font-size: 16px;
`;

const HighlightArea = styled.div`
    background-color: #FFF2CC;
    padding: 20px;
    border-radius: 10px;
`;

const SmallHighlightArea = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 2px solid #FFF2CC;
    padding: 0 10px;
    border-radius: 10px;
    margin-top: 40px;
`;

const StyledPrimaryButton = styled(PrimaryButton)`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 20px;
    width: 100px;
`;